/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';
import {
  getFeeClaimRoleDecoder,
  getFeeClaimRoleEncoder,
  type FeeClaimRole,
  type FeeClaimRoleArgs,
} from '../types';

export const CLAIM_FEES_DISCRIMINATOR = new Uint8Array([
  82, 251, 233, 156, 12, 52, 184, 202,
]);

export function getClaimFeesDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(CLAIM_FEES_DISCRIMINATOR);
}

export type ClaimFeesInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountLaunch extends string | AccountMeta<string> = string,
  TAccountLaunchFeeState extends string | AccountMeta<string> = string,
  TAccountLaunchAuthority extends string | AccountMeta<string> = string,
  TAccountBaseMint extends string | AccountMeta<string> = string,
  TAccountQuoteMint extends string | AccountMeta<string> = string,
  TAccountBaseVault extends string | AccountMeta<string> = string,
  TAccountQuoteVault extends string | AccountMeta<string> = string,
  TAccountRecipientBaseAta extends string | AccountMeta<string> = string,
  TAccountRecipientQuoteAta extends string | AccountMeta<string> = string,
  TAccountBaseTokenProgram extends string | AccountMeta<string> = string,
  TAccountQuoteTokenProgram extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountLaunch extends string
        ? ReadonlyAccount<TAccountLaunch>
        : TAccountLaunch,
      TAccountLaunchFeeState extends string
        ? WritableAccount<TAccountLaunchFeeState>
        : TAccountLaunchFeeState,
      TAccountLaunchAuthority extends string
        ? ReadonlyAccount<TAccountLaunchAuthority>
        : TAccountLaunchAuthority,
      TAccountBaseMint extends string
        ? ReadonlyAccount<TAccountBaseMint>
        : TAccountBaseMint,
      TAccountQuoteMint extends string
        ? ReadonlyAccount<TAccountQuoteMint>
        : TAccountQuoteMint,
      TAccountBaseVault extends string
        ? WritableAccount<TAccountBaseVault>
        : TAccountBaseVault,
      TAccountQuoteVault extends string
        ? WritableAccount<TAccountQuoteVault>
        : TAccountQuoteVault,
      TAccountRecipientBaseAta extends string
        ? WritableAccount<TAccountRecipientBaseAta>
        : TAccountRecipientBaseAta,
      TAccountRecipientQuoteAta extends string
        ? WritableAccount<TAccountRecipientQuoteAta>
        : TAccountRecipientQuoteAta,
      TAccountBaseTokenProgram extends string
        ? ReadonlyAccount<TAccountBaseTokenProgram>
        : TAccountBaseTokenProgram,
      TAccountQuoteTokenProgram extends string
        ? ReadonlyAccount<TAccountQuoteTokenProgram>
        : TAccountQuoteTokenProgram,
      ...TRemainingAccounts,
    ]
  >;

export type ClaimFeesInstructionData = {
  discriminator: ReadonlyUint8Array;
  role: FeeClaimRole;
};

export type ClaimFeesInstructionDataArgs = { role: FeeClaimRoleArgs };

export function getClaimFeesInstructionDataEncoder(): FixedSizeEncoder<ClaimFeesInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['role', getFeeClaimRoleEncoder()],
    ]),
    (value) => ({ ...value, discriminator: CLAIM_FEES_DISCRIMINATOR }),
  );
}

export function getClaimFeesInstructionDataDecoder(): FixedSizeDecoder<ClaimFeesInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['role', getFeeClaimRoleDecoder()],
  ]);
}

export function getClaimFeesInstructionDataCodec(): FixedSizeCodec<
  ClaimFeesInstructionDataArgs,
  ClaimFeesInstructionData
> {
  return combineCodec(
    getClaimFeesInstructionDataEncoder(),
    getClaimFeesInstructionDataDecoder(),
  );
}

export type ClaimFeesAsyncInput<
  TAccountConfig extends string = string,
  TAccountLaunch extends string = string,
  TAccountLaunchFeeState extends string = string,
  TAccountLaunchAuthority extends string = string,
  TAccountBaseMint extends string = string,
  TAccountQuoteMint extends string = string,
  TAccountBaseVault extends string = string,
  TAccountQuoteVault extends string = string,
  TAccountRecipientBaseAta extends string = string,
  TAccountRecipientQuoteAta extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountQuoteTokenProgram extends string = string,
> = {
  config?: Address<TAccountConfig>;
  launch: Address<TAccountLaunch>;
  launchFeeState?: Address<TAccountLaunchFeeState>;
  launchAuthority?: Address<TAccountLaunchAuthority>;
  baseMint: Address<TAccountBaseMint>;
  quoteMint: Address<TAccountQuoteMint>;
  baseVault: Address<TAccountBaseVault>;
  quoteVault: Address<TAccountQuoteVault>;
  recipientBaseAta: Address<TAccountRecipientBaseAta>;
  recipientQuoteAta: Address<TAccountRecipientQuoteAta>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  quoteTokenProgram: Address<TAccountQuoteTokenProgram>;
  role: ClaimFeesInstructionDataArgs['role'];
};

export async function getClaimFeesInstructionAsync<
  TAccountConfig extends string,
  TAccountLaunch extends string,
  TAccountLaunchFeeState extends string,
  TAccountLaunchAuthority extends string,
  TAccountBaseMint extends string,
  TAccountQuoteMint extends string,
  TAccountBaseVault extends string,
  TAccountQuoteVault extends string,
  TAccountRecipientBaseAta extends string,
  TAccountRecipientQuoteAta extends string,
  TAccountBaseTokenProgram extends string,
  TAccountQuoteTokenProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: ClaimFeesAsyncInput<
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountLaunchAuthority,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountRecipientBaseAta,
    TAccountRecipientQuoteAta,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  ClaimFeesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountLaunchAuthority,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountRecipientBaseAta,
    TAccountRecipientQuoteAta,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    launch: { value: input.launch ?? null, isWritable: false },
    launchFeeState: { value: input.launchFeeState ?? null, isWritable: true },
    launchAuthority: {
      value: input.launchAuthority ?? null,
      isWritable: false,
    },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    quoteMint: { value: input.quoteMint ?? null, isWritable: false },
    baseVault: { value: input.baseVault ?? null, isWritable: true },
    quoteVault: { value: input.quoteVault ?? null, isWritable: true },
    recipientBaseAta: {
      value: input.recipientBaseAta ?? null,
      isWritable: true,
    },
    recipientQuoteAta: {
      value: input.recipientQuoteAta ?? null,
      isWritable: true,
    },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    quoteTokenProgram: {
      value: input.quoteTokenProgram ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([99, 111, 110, 102, 105, 103])),
      ],
    });
  }
  if (!accounts.launchFeeState.value) {
    accounts.launchFeeState.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            108, 97, 117, 110, 99, 104, 95, 102, 101, 101, 95, 115, 116, 97,
            116, 101,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'launch',
            accounts.launch.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.launchAuthority.value) {
    accounts.launchAuthority.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            108, 97, 117, 110, 99, 104, 95, 97, 117, 116, 104, 111, 114, 105,
            116, 121,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'launch',
            accounts.launch.value,
          ),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchFeeState', accounts.launchFeeState),
      getAccountMeta('launchAuthority', accounts.launchAuthority),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('quoteMint', accounts.quoteMint),
      getAccountMeta('baseVault', accounts.baseVault),
      getAccountMeta('quoteVault', accounts.quoteVault),
      getAccountMeta('recipientBaseAta', accounts.recipientBaseAta),
      getAccountMeta('recipientQuoteAta', accounts.recipientQuoteAta),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('quoteTokenProgram', accounts.quoteTokenProgram),
    ],
    data: getClaimFeesInstructionDataEncoder().encode(
      args as ClaimFeesInstructionDataArgs,
    ),
    programAddress,
  } as ClaimFeesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountLaunchAuthority,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountRecipientBaseAta,
    TAccountRecipientQuoteAta,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >);
}

export type ClaimFeesInput<
  TAccountConfig extends string = string,
  TAccountLaunch extends string = string,
  TAccountLaunchFeeState extends string = string,
  TAccountLaunchAuthority extends string = string,
  TAccountBaseMint extends string = string,
  TAccountQuoteMint extends string = string,
  TAccountBaseVault extends string = string,
  TAccountQuoteVault extends string = string,
  TAccountRecipientBaseAta extends string = string,
  TAccountRecipientQuoteAta extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountQuoteTokenProgram extends string = string,
> = {
  config: Address<TAccountConfig>;
  launch: Address<TAccountLaunch>;
  launchFeeState: Address<TAccountLaunchFeeState>;
  launchAuthority: Address<TAccountLaunchAuthority>;
  baseMint: Address<TAccountBaseMint>;
  quoteMint: Address<TAccountQuoteMint>;
  baseVault: Address<TAccountBaseVault>;
  quoteVault: Address<TAccountQuoteVault>;
  recipientBaseAta: Address<TAccountRecipientBaseAta>;
  recipientQuoteAta: Address<TAccountRecipientQuoteAta>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  quoteTokenProgram: Address<TAccountQuoteTokenProgram>;
  role: ClaimFeesInstructionDataArgs['role'];
};

export function getClaimFeesInstruction<
  TAccountConfig extends string,
  TAccountLaunch extends string,
  TAccountLaunchFeeState extends string,
  TAccountLaunchAuthority extends string,
  TAccountBaseMint extends string,
  TAccountQuoteMint extends string,
  TAccountBaseVault extends string,
  TAccountQuoteVault extends string,
  TAccountRecipientBaseAta extends string,
  TAccountRecipientQuoteAta extends string,
  TAccountBaseTokenProgram extends string,
  TAccountQuoteTokenProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: ClaimFeesInput<
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountLaunchAuthority,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountRecipientBaseAta,
    TAccountRecipientQuoteAta,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): ClaimFeesInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountLaunch,
  TAccountLaunchFeeState,
  TAccountLaunchAuthority,
  TAccountBaseMint,
  TAccountQuoteMint,
  TAccountBaseVault,
  TAccountQuoteVault,
  TAccountRecipientBaseAta,
  TAccountRecipientQuoteAta,
  TAccountBaseTokenProgram,
  TAccountQuoteTokenProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    launch: { value: input.launch ?? null, isWritable: false },
    launchFeeState: { value: input.launchFeeState ?? null, isWritable: true },
    launchAuthority: {
      value: input.launchAuthority ?? null,
      isWritable: false,
    },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    quoteMint: { value: input.quoteMint ?? null, isWritable: false },
    baseVault: { value: input.baseVault ?? null, isWritable: true },
    quoteVault: { value: input.quoteVault ?? null, isWritable: true },
    recipientBaseAta: {
      value: input.recipientBaseAta ?? null,
      isWritable: true,
    },
    recipientQuoteAta: {
      value: input.recipientQuoteAta ?? null,
      isWritable: true,
    },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    quoteTokenProgram: {
      value: input.quoteTokenProgram ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchFeeState', accounts.launchFeeState),
      getAccountMeta('launchAuthority', accounts.launchAuthority),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('quoteMint', accounts.quoteMint),
      getAccountMeta('baseVault', accounts.baseVault),
      getAccountMeta('quoteVault', accounts.quoteVault),
      getAccountMeta('recipientBaseAta', accounts.recipientBaseAta),
      getAccountMeta('recipientQuoteAta', accounts.recipientQuoteAta),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('quoteTokenProgram', accounts.quoteTokenProgram),
    ],
    data: getClaimFeesInstructionDataEncoder().encode(
      args as ClaimFeesInstructionDataArgs,
    ),
    programAddress,
  } as ClaimFeesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountLaunchAuthority,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountRecipientBaseAta,
    TAccountRecipientQuoteAta,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >);
}

export type ParsedClaimFeesInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    launch: TAccountMetas[1];
    launchFeeState: TAccountMetas[2];
    launchAuthority: TAccountMetas[3];
    baseMint: TAccountMetas[4];
    quoteMint: TAccountMetas[5];
    baseVault: TAccountMetas[6];
    quoteVault: TAccountMetas[7];
    recipientBaseAta: TAccountMetas[8];
    recipientQuoteAta: TAccountMetas[9];
    baseTokenProgram: TAccountMetas[10];
    quoteTokenProgram: TAccountMetas[11];
  };
  data: ClaimFeesInstructionData;
};

export function parseClaimFeesInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedClaimFeesInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 12) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 12,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      launch: getNextAccount(),
      launchFeeState: getNextAccount(),
      launchAuthority: getNextAccount(),
      baseMint: getNextAccount(),
      quoteMint: getNextAccount(),
      baseVault: getNextAccount(),
      quoteVault: getNextAccount(),
      recipientBaseAta: getNextAccount(),
      recipientQuoteAta: getNextAccount(),
      baseTokenProgram: getNextAccount(),
      quoteTokenProgram: getNextAccount(),
    },
    data: getClaimFeesInstructionDataDecoder().decode(instruction.data),
  };
}
